import React, { useState, useMemo, useCallback } from 'react';
import { useLocalNavigation } from '../../context/NavigationProvider';
import {
  AuthHeader,
  NoAuth,
  OfflineHeader,
  UpdateAvailable,
  SettingsPanel,
  AppLoader,
  Dialog,
  DialogContent,
} from '@jaam/web-core';
import payrollrIcon from '../../assets/icons/payrollr.svg';
import { useFetchCompanies } from '../../hooks/useCompanies';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { authState } from '../../state/authState';
import { companyState } from '../../state/companyState';
import {
  SidebarItemsModel,
  useSettingsPanel,
  useSelectCompany,
  useAbility,
} from '@jaam/web-core';
import PAYROLL_ITEMS from '../../constants/payrollSidebarItems';
import Sidebar from '../../shared-components/layout/sidebar/sidebar';
import { useAuth } from '../../hooks/useAuth';
import { useOnlineStatus } from '../../context/navigation/OnlineStatusContext';
import CreateCompany from '../../pages/company/create/createCompany';

const NAV_ITEMS = PAYROLL_ITEMS as SidebarItemsModel[];

const SUSPENSE_FALLBACK = (
  <div className="h-screen w-screen flex justify-center items-center xl:mr-[217px] lg:mr-[100px]">
    <AppLoader />
  </div>
);

interface Iprop {
  children?: JSX.Element | null;
}

const MainLayout = (props: Iprop) => {
  const { children } = props;

  const { logout, isAuthenticated } = useAuth();
  const { companies, isFetchingCompanies } = useFetchCompanies({
    searchTerm: '',
    page: 1,
    limit: 10,
    isAuthenticated,
  });
  const auth = useRecoilValue(authState);
  const companyData = useRecoilValue(companyState);
  const setCompanyState = useSetRecoilState(companyState);
  const ability = useAbility();
  const user = auth?.user;
  const { showBackButton, backRoute, showSideMenu } = useLocalNavigation();
  const { closeSettingsPanel, setCloseSettingsPanel } = useSettingsPanel();
  const { onlineStatus, updateWaiting, handleUpdate } = useOnlineStatus();

  const [handleCompanyClick] = useSelectCompany();
  const [newCompany, setNewCompany] = useState(false);

  const handleCloseNewCompanyDialog = useCallback(() => {
    setNewCompany(false);
    setCompanyState((prev) => ({ ...prev, createWithModal: false }));
  }, [setCompanyState]);

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      setNewCompany(open);
      if (!open) handleCloseNewCompanyDialog();
    },
    [handleCloseNewCompanyDialog],
  );

  const handleCompanyClickWithOnlineCheck = useCallback(
    (e: Parameters<typeof handleCompanyClick>[0]) => {
      if (navigator.onLine) handleCompanyClick(e);
    },
    [handleCompanyClick],
  );

  const handleNewCompanyOpen = useCallback(() => {
    setCompanyState((prev) => ({ ...prev, createWithModal: true }));
    setNewCompany(true);
  }, [setCompanyState]);

  const menu = useMemo(
    () =>
      companyData.activeCompany && user ? (
        <SettingsPanel
          ability={ability}
          user={user}
          setCloseSettingsPanel={setCloseSettingsPanel}
          activeCompany={companyData.activeCompany}
          companies={companies}
          handleCompanyClick={handleCompanyClickWithOnlineCheck}
          isLoadingCompanies={isFetchingCompanies}
          logoutHandler={logout}
          handleNewCompany={handleNewCompanyOpen}
        />
      ) : null,
    [
      ability,
      user,
      setCloseSettingsPanel,
      companyData.activeCompany,
      companies,
      handleCompanyClickWithOnlineCheck,
      isFetchingCompanies,
      logout,
      handleNewCompanyOpen,
    ],
  );

  return (
    <div className="flex flex-col w-screen h-screen">
      {!onlineStatus && <OfflineHeader />}
      {updateWaiting && <UpdateAvailable handle={handleUpdate} />}

      <Dialog open={newCompany} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[calc(100vh-20px)] overflow-y-auto">
          <CreateCompany
            cancel={handleCloseNewCompanyDialog}
            firstCreation={false}
          />
        </DialogContent>
      </Dialog>

      {companyData.activeCompany ? (
        <div className="w-screen h-screen ">
          <AuthHeader
            showBackButton={showBackButton}
            backRoute={backRoute}
            closeSettingsPanel={closeSettingsPanel}
            setCloseSettingsPanel={setCloseSettingsPanel}
            company={companyData.activeCompany}
            user={user!}
            appIconUrl={payrollrIcon}
            menu={menu}
          />
          <main
            className={`
            } max-w-[1440px] md:px-12  relative z-[1] bg-neutral-98 md:mx-auto mx-4 flex flex-row  max-h-[calc(100vh-112px)] h-full md:gap-6  mb-9 `}
          >
            {!showSideMenu && (
              <div className="flex h-full md:mt-9">
                <Sidebar SidebarItems={NAV_ITEMS} />
              </div>
            )}
            <React.Suspense fallback={SUSPENSE_FALLBACK}>
              <div className="w-full h-full">{children}</div>
            </React.Suspense>
          </main>
        </div>
      ) : (
        <div className="max-w-[1440px] flex flex-col h-screen bg-gradient-to-r from-[#5f6f6] to-[#d7d9dc] pt-[60px] overflow-y-scroll pb-[5rem]">
          <NoAuth />
          {children}
        </div>
      )}
    </div>
  );
};

export default React.memo(MainLayout);
